import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import AdventureCard from "../components/AdventureCard";
import AdSlot from "../components/AdSlot";
import BrandOfTheWeek from "../components/BrandOfTheWeek";
import OnboardingQuest from "../components/OnboardingQuest";
import TrailPioneers from "../components/TrailPioneers";
import FriendChallengeSocialChip from "../components/FriendChallengeSocialChip";
import { TopBar } from "../components/Navigation";
import { useTierGate } from "../lib/PaywallContext";
import {
    Filter, TrendingUp, Clock, MapPin, Users, Plus, Sparkles, Compass, Lock,
} from "lucide-react";

const EXPLORE_FILTERS = [
    { id: "newest", label: "Newest", icon: Clock },
    { id: "popular", label: "Popular", icon: TrendingUp },
    { id: "nearby", label: "Nearby", icon: MapPin },
    { id: "following", label: "Following", icon: Users },
];

export default function Feed() {
    const { user } = useAuth();
    const gate = useTierGate(user);
    const isFree = user && user.role !== "admin" && (user.tier || "free") === "free";
    const [tab, setTab] = useState("for-you");
    const [filter, setFilter] = useState("newest");
    const [exploreItems, setExploreItems] = useState([]);
    const [forYouItems, setForYouItems] = useState([]);
    const [forYouMeta, setForYouMeta] = useState({ matched: 0 });
    const [loading, setLoading] = useState(true);

    // Explore feed (chronological / filtered)
    useEffect(() => {
        if (tab !== "explore") return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/adventures`, { params: { filter } });
                if (!cancelled) setExploreItems(data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [filter, tab]);

    // For You feed (tailored recommendations)
    useEffect(() => {
        if (tab !== "for-you") return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/recommendations/adventures`, { params: { limit: 24 } });
                if (!cancelled) {
                    setForYouItems(data.items || []);
                    setForYouMeta({ matched: data.matched || 0 });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [tab]);

    const toggleLike = async (adv, source) => {
        try {
            const { data } = await api.post(`/adventures/${adv.id}/like`);
            const mutate = (x) =>
                x.id === adv.id
                    ? { ...x, liked: data.liked, likes_count: x.likes_count + (data.liked ? 1 : -1) }
                    : x;
            if (source === "for-you") setForYouItems((xs) => xs.map(mutate));
            else setExploreItems((xs) => xs.map(mutate));
        } catch { /* ignore */ }
    };

    const toggleSave = async (adv, source) => {
        try {
            const { data } = await api.post(`/adventures/${adv.id}/save`);
            const mutate = (x) =>
                x.id === adv.id
                    ? { ...x, saved: data.saved, saves_count: x.saves_count + (data.saved ? 1 : -1) }
                    : x;
            if (source === "for-you") setForYouItems((xs) => xs.map(mutate));
            else setExploreItems((xs) => xs.map(mutate));
        } catch { /* ignore */ }
    };

    const showAds = user?.tier !== "premium";
    const items = tab === "for-you" ? forYouItems : exploreItems;

    return (
        <div>
            <TopBar
                title="Feed"
                subtitle={tab === "for-you" ? "Adventures tailored to you" : `${exploreItems.length} fresh adventures`}
            />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                {/* Header */}
                <div className="hidden lg:flex items-end justify-between mb-8">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">
                            Welcome back, {user?.name?.split(" ")[0]}
                        </div>
                        <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">
                            Fresh from the trail
                        </h1>
                    </div>
                    <Link
                        to="/create"
                        data-testid="feed-create-btn"
                        onClick={(e) => {
                            if (isFree) {
                                e.preventDefault();
                                gate({ required_tier: "adventure", feature: "post adventures" });
                            }
                        }}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-95 transition-all"
                    >
                        {isFree ? <Lock className="w-4 h-4" /> : <Plus className="w-4 h-4" />} New adventure
                    </Link>
                </div>

                {/* Tabs */}
                <div
                    role="tablist"
                    aria-label="Feed tabs"
                    data-testid="feed-tabs"
                    className="inline-flex items-center gap-1 p-1 rounded-full bg-snowcap border border-ridge-line mb-6"
                >
                    <TabButton
                        id="for-you"
                        testid="feed-tab-for-you"
                        active={tab === "for-you"}
                        onClick={() => setTab("for-you")}
                        icon={Sparkles}
                        label="For You"
                    />
                    <TabButton
                        id="explore"
                        testid="feed-tab-explore"
                        active={tab === "explore"}
                        onClick={() => setTab("explore")}
                        icon={Compass}
                        label="Explore"
                    />
                </div>

                {/* Explore filters (only when on Explore tab) */}
                {tab === "explore" && (
                    <div
                        data-testid="explore-filter-row"
                        className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4 lg:mx-0 lg:px-0"
                    >
                        {EXPLORE_FILTERS.map((f) => {
                            const active = filter === f.id;
                            return (
                                <button
                                    key={f.id}
                                    data-testid={`feed-filter-${f.id}`}
                                    onClick={() => setFilter(f.id)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold shrink-0 transition-all ${
                                        active
                                            ? "bg-midnight-pine text-white"
                                            : "bg-snowcap text-granite border border-ridge-line hover:border-midnight-pine"
                                    }`}
                                >
                                    <f.icon className="w-4 h-4" /> {f.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Content grid */}
                <div className="grid lg:grid-cols-[1fr,280px] gap-6">
                    <div data-testid={`feed-panel-${tab}`}>
                        <BrandOfTheWeek />
                        {tab === "for-you" && <FreshPrefsBanner user={user} />}
                        {tab === "for-you" && <FriendChallengeSocialChip />}
                        {loading ? (
                            <SkeletonGrid />
                        ) : tab === "for-you" && items.length === 0 ? (
                            <ForYouEmpty hasPreferences={!!(user?.interests?.length || user?.adventure_style)} />
                        ) : tab === "explore" && items.length === 0 ? (
                            <ExploreEmpty filter={filter} />
                        ) : (
                            <div className="grid md:grid-cols-2 gap-5">
                                {items.map((adv, i) => (
                                    <React.Fragment key={adv.id}>
                                        <AdventureCard
                                            adventure={adv}
                                            onLike={(a) => toggleLike(a, tab)}
                                            onSave={(a) => toggleSave(a, tab)}
                                            matchReasons={tab === "for-you" ? adv.match_reasons : undefined}
                                        />
                                        {showAds && (i + 1) % 4 === 0 && (
                                            <div className="md:col-span-2">
                                                <AdSlot placement="feed" />
                                            </div>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        {tab === "for-you" && forYouItems.length > 0 && (
                            <div className="mt-8 p-4 rounded-md bg-snowcap border border-ridge-line text-center">
                                <p className="text-sm text-slate-gray">
                                    Want to see everything?{" "}
                                    <button
                                        data-testid="for-you-switch-explore"
                                        onClick={() => setTab("explore")}
                                        className="font-bold text-ember-orange hover:underline"
                                    >
                                        Browse all adventures →
                                    </button>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <aside className="hidden lg:block space-y-5">
                        <OnboardingQuest />

                        <TrailPioneers />

                        <div className="bg-snowcap border border-ridge-line rounded-lg p-5">
                            <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-gray mb-3">
                                Your stats
                            </div>
                            <div className="space-y-3">
                                <StatRow label="Followers" value={user?.followers_count || 0} />
                                <StatRow label="Following" value={user?.following_count || 0} />
                                <StatRow label="Tier" value={user?.tier || "free"} highlight />
                            </div>
                            <Link
                                to="/profile"
                                data-testid="feed-view-profile"
                                className="mt-5 block text-center py-2 rounded-md border border-midnight-pine text-midnight-pine font-bold text-sm hover:bg-midnight-pine hover:text-white transition-all"
                            >
                                View my profile
                            </Link>
                        </div>

                        {tab === "for-you" && forYouMeta.matched > 0 && (
                            <div className="bg-snowcap border border-ridge-line rounded-lg p-5" data-testid="for-you-meta-card">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="w-4 h-4 text-ember-orange" />
                                    <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-gray">
                                        Tailored feed
                                    </span>
                                </div>
                                <p className="text-sm text-granite leading-relaxed mb-3">
                                    {forYouMeta.matched} adventure{forYouMeta.matched === 1 ? "" : "s"} scored against your interests.
                                </p>
                                <Link
                                    to="/profile/edit"
                                    data-testid="for-you-tune-preferences"
                                    className="block text-center py-2 rounded-md border border-midnight-pine text-midnight-pine font-bold text-xs hover:bg-midnight-pine hover:text-white transition-all"
                                >
                                    Tune preferences
                                </Link>
                            </div>
                        )}

                        {user?.tier !== "premium" && (
                            <div className="bg-midnight-pine text-white rounded-lg p-5">
                                <div className="text-xs font-bold uppercase tracking-[0.25em] text-ember-orange mb-2">
                                    Go Premium
                                </div>
                                <p className="text-sm text-white/80 mb-4 leading-relaxed">
                                    Unlock full GPS, marketplace, no ads, and a Premium badge.
                                </p>
                                <Link
                                    to="/pricing"
                                    data-testid="feed-upgrade-btn"
                                    className="block text-center py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 transition-all"
                                >
                                    See plans
                                </Link>
                            </div>
                        )}

                        {showAds && <AdSlot variant="sidebar" placement="sidebar" />}
                    </aside>
                </div>
            </div>
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            role="tab"
            aria-selected={active}
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${
                active
                    ? "bg-midnight-pine text-white shadow-sm"
                    : "text-granite hover:text-midnight-pine"
            }`}
        >
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}

function SkeletonGrid() {
    return (
        <div className="grid md:grid-cols-2 gap-5" data-testid="feed-skeleton">
            {[0, 1, 2, 3].map((i) => (
                <div key={i} className="bg-snowcap border border-ridge-line rounded-lg overflow-hidden">
                    <div className="aspect-[16/10] bg-trail-tan/50 animate-pulse" />
                    <div className="p-5 space-y-2">
                        <div className="h-4 bg-ridge-line rounded animate-pulse w-3/4" />
                        <div className="h-3 bg-ridge-line rounded animate-pulse w-1/2" />
                    </div>
                </div>
            ))}
        </div>
    );
}

function ForYouEmpty({ hasPreferences }) {
    return (
        <div
            className="bg-snowcap border border-ridge-line rounded-lg p-12 text-center"
            data-testid="for-you-empty"
        >
            <Sparkles className="w-10 h-10 text-ember-orange mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-midnight-pine mb-2">
                {hasPreferences ? "Nothing new to match yet" : "Help us tailor your feed"}
            </h3>
            <p className="text-slate-gray mb-5 max-w-sm mx-auto">
                {hasPreferences
                    ? "We'll surface adventures here as they get posted. In the meantime, browse Explore."
                    : "Tell us what kind of outdoors you love and we'll surface adventures hand-picked for you."}
            </p>
            <div className="inline-flex flex-wrap justify-center gap-2">
                {!hasPreferences && (
                    <Link
                        to="/profile/edit"
                        data-testid="for-you-empty-edit-prefs"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark transition-all"
                    >
                        Set preferences
                    </Link>
                )}
                <Link
                    to="/explore"
                    data-testid="for-you-empty-explore"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-midnight-pine text-midnight-pine font-bold hover:bg-midnight-pine hover:text-white transition-all"
                >
                    <Compass className="w-4 h-4" /> Explore all
                </Link>
            </div>
        </div>
    );
}

function ExploreEmpty({ filter }) {
    return (
        <div className="bg-snowcap border border-ridge-line rounded-lg p-12 text-center" data-testid="explore-empty">
            <Filter className="w-10 h-10 text-slate-gray mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-midnight-pine mb-2">No adventures yet</h3>
            <p className="text-slate-gray mb-5">
                {filter === "following"
                    ? "Follow more people to see their adventures here."
                    : "Be the first to share something."}
            </p>
            <Link
                to="/create"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark transition-all"
            >
                <Plus className="w-4 h-4" /> Post an adventure
            </Link>
        </div>
    );
}

function StatRow({ label, value, highlight }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-sm text-slate-gray">{label}</span>
            <span className={`font-display font-black ${highlight ? "text-ember-orange capitalize" : "text-midnight-pine"}`}>
                {value}
            </span>
        </div>
    );
}

function FreshPrefsBanner({ user }) {
    const [dismissed, setDismissed] = useState(false);
    if (!user || dismissed) return null;
    const dismissKey = `prefs-banner-dismissed-${user.user_id}`;
    if (typeof window !== "undefined") {
        const ts = Number(window.localStorage.getItem(dismissKey) || 0);
        if (ts && Date.now() - ts < 14 * 24 * 60 * 60 * 1000) return null; // snoozed 14 days
    }
    const lastStr = user.preferences_updated_at || "";
    const lastMs = lastStr ? new Date(lastStr).getTime() : 0;
    const stale = !lastMs || Date.now() - lastMs > 60 * 24 * 60 * 60 * 1000; // 60 days
    if (!stale) return null;
    const snooze = () => {
        setDismissed(true);
        try { window.localStorage.setItem(dismissKey, String(Date.now())); } catch { /* quota */ }
    };
    return (
        <div
            data-testid="fresh-prefs-banner"
            className="mb-5 p-4 rounded-lg bg-trail-tan/40 border border-trail-tan flex items-center gap-3"
        >
            <Sparkles className="w-5 h-5 text-ember-orange shrink-0" />
            <div className="flex-1 text-sm text-granite leading-snug">
                Keep your feed fresh — update your trail preferences so we can surface what you're into now.
            </div>
            <Link
                to="/profile/edit"
                data-testid="fresh-prefs-cta"
                className="px-3 py-1.5 rounded-md bg-midnight-pine text-white text-xs font-bold hover:bg-midnight-pine/90 whitespace-nowrap"
            >
                Update
            </Link>
            <button
                type="button"
                data-testid="fresh-prefs-dismiss"
                onClick={snooze}
                className="text-xs font-bold text-slate-gray hover:text-midnight-pine whitespace-nowrap"
            >
                Later
            </button>
        </div>
    );
}
